"use client";

import { useState, useRef, useEffect } from "react";
import { useLocale } from "next-intl";
import { useRouter, usePathname } from "@/i18n/navigation";
import { routing, type Locale } from "@/i18n/routing";
import { FiGlobe, FiChevronDown } from "react-icons/fi";

const localeLabels: Record<string, string> = {
  en: "EN",
  es: "ES",
  de: "DE",
  nl: "NL",
  pap: "PAP",
};

const localeNames: Record<string, string> = {
  en: "English",
  es: "Español",
  de: "Deutsch",
  nl: "Nederlands",
  pap: "Papiamentu",
};

export default function LanguageSwitcher() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function switchLocale(newLocale: Locale) {
    router.replace(pathname, { locale: newLocale });
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-fbtt-navy transition-colors hover:bg-fbtt-light"
        aria-label="Switch language"
      >
        <FiGlobe className="h-4 w-4" />
        <span className="font-medium">{localeLabels[locale]}</span>
        <FiChevronDown className="h-3 w-3" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-1 min-w-[140px] rounded-lg border border-fbtt-gray-light bg-white py-1 shadow-lg">
          {routing.locales.map((loc) => (
            <button
              key={loc}
              onClick={() => switchLocale(loc)}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-fbtt-light ${
                loc === locale
                  ? "font-semibold text-fbtt-orange"
                  : "text-fbtt-navy"
              }`}
            >
              <span className="w-6 font-medium text-fbtt-gray">
                {localeLabels[loc]}
              </span>
              {localeNames[loc]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
